import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { FloatingFormInput } from '~/components/form/FloatingFormInput';
import { FloatingLabel } from '~/components/form/FloatingLabel';
import { StatusPill } from './StatusPill';
import { FormError } from '~/components/form/FormError';
import { Navigate, useParams } from "react-router-dom";
import { newUserSetPassword } from "~/api/settings/newUserSetPassword";
import Error404 from '~/components/layout/Error404';
import { CheckCircle, ShieldOutlined } from '@mui/icons-material';
const formSchema = yup
    .object({
    password: yup
        .string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/\d/, 'Password must contain at least one number')
        .matches(/[!"#$%&'()*+,./:;<=>?@[\]^_`{}~\-]/, 'Password must contain at least one special character'),
    passwordConfirmation: yup
        .string()
        .oneOf([yup.ref('password'), null], 'Passwords must match'),
    phone: yup
        .string()
        .required('Phone is required')
        .matches(/^\d{3}-\d{3}-\d{4}$/, 'Invalid phone number'),
})
    .required();
function getPasswordState(password, passwordConfirmation) {
    const state = {
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        digit: /\d/.test(password),
        special: /[!"#$%&'()*+,./:;<=>?@[\]^_`{}~\-]/.test(password),
    };
    const strength = Object.values(state).reduce((acc, curr) => {
        return curr ? acc + 1 : acc;
    }, 0);
    const match = !!password && password === passwordConfirmation;
    return { ...state, strength, match };
}
const formatPhone = (input) => {
    const digits = input.replace(/\D/g, '').slice(0, 10);
    if (digits.length < 4)
        return digits;
    if (digits.length < 7)
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};
const NewUserSetPassword = () => {
    const { email, hash } = useParams();
    const [sendToLogin, setSendToLogin] = useState(false);
    const [sendToAegisDashboard, setSendToAegisDashboard] = useState(false);
    const [state, setState] = useState({ loading: true, valid: false, name: null, role_name: null, team_name: null, access_level: null });
    const [showSuccess, setShowSuccess] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    if (!email) {
        throw new Error('Email is required');
    }
    if (!hash) {
        throw new Error('Password token is required');
    }
    useEffect(() => {
        fetch(`/api/auth/validateSetup/${encodeURIComponent(email)}/${hash}`)
            .then(res => res.json())
            .then(data => setState({ loading: false, valid: data.valid, name: data.name, role_name: data.role_name, team_name: data.team_name, access_level: data.access_level }))
            .catch(() => setState({ loading: false, valid: false, name: null, role_name: null, team_name: null, access_level: null }));
    }, [email, hash]);
    const methods = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: {
            password: '',
            passwordConfirmation: '',
            email: email,
            hash: hash,
            phone: ''
        },
    });
    const { formState: { isSubmitting, errors }, handleSubmit, register, watch, clearErrors, setValue } = methods;
    const onSubmit = async (formData) => {
        const newP = formData.password;
        const confirm = formData.passwordConfirmation;
        // const phone = formData.phone;
        const response = await newUserSetPassword({ newPassword: newP, confirmNewPassword: confirm, email: email, hash: hash, phone: phone });
        setShowSuccess(true);
        setResponseMessage(response.message);
        // alert('Success! Please login with your new credentials.');
    };
    const { password, passwordConfirmation, phone } = watch();
    const passwordState = getPasswordState(password, passwordConfirmation);
    const disableSubmit = !passwordState.match || !passwordState.length || !passwordState.digit || !passwordState.special || !passwordState.uppercase;
    const handlePhoneChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        const formatted = formatPhone(raw);
        setValue('phone', formatted, { shouldValidate: true });
        if (raw.length === 10)
            clearErrors('phone');
    };
    const handleContinueClick = () => {
        if (responseMessage === 'Aegis') {
            setSendToAegisDashboard(true);
        }
        else {
            setSendToLogin(true);
        }
    };
    if (sendToLogin) {
        return React.createElement(Navigate, { to: '/login' });
    }
    if (sendToAegisDashboard) {
        return React.createElement(Navigate, { to: '/aegisDashboard' });
    }
    if (state.loading) {
        return React.createElement("div", null, "Loading\u2026");
    }
    if (!state.valid) {
        return React.createElement(Error404, null);
    }
    return (React.createElement(React.Fragment, null, showSuccess ?
        React.createElement("div", { style: { backgroundColor: '#FAFAFA' } },
            React.createElement("div", { style: { backgroundColor: '#FAFAFA' } },
                React.createElement("img", { src: "/images/ss_logo_blue.png", className: "mb-10", alt: "SweetSpot", width: "225", height: "45" }),
                React.createElement("div", { style: { textAlign: 'center' } },
                    React.createElement(CheckCircle, { style: { marginLeft: 'auto', marginRight: 'auto', width: '80px', height: '80px', color: '#0F9D58' } })),
                React.createElement("div", { style: { color: 'black', fontSize: 48, fontFamily: 'Barlow', fontWeight: '500', textAlign: 'center' } }, "Welcome to SweetSpot!"),
                React.createElement("div", { style: { color: '#5F6368', fontSize: 18, fontFamily: 'Barlow', fontWeight: '400', textAlign: 'center' } }, "Your account has been successfully created. You're all set to start using SweetSpot."),
                React.createElement("div", { style: { marginTop: '25px', width: '550px', marginLeft: 'auto', marginRight: 'auto', backgroundColor: '#FFFFFF', borderColor: '#E0E0E0', borderWidth: '1px', borderRadius: '4px', padding: '25px' } },
                    React.createElement("div", { style: { display: 'flex', marginBottom: '10px' } },
                        React.createElement("div", { style: { marginRight: 'auto', fontWeight: '400', color: '#5F6368', fontFamily: 'Barlow', fontSize: 14 } }, "Name:"),
                        React.createElement("div", { style: { marginLeft: 'auto', fontWeight: '500', color: '#1A1A1A', fontFamily: 'Barlow', fontSize: 14 } }, state.name)),
                    React.createElement("div", { style: { display: 'flex', marginTop: '10px', marginBottom: '10px' } },
                        React.createElement("div", { style: { marginRight: 'auto', fontWeight: '400', color: '#5F6368', fontFamily: 'Barlow', fontSize: 14 } }, "Email:"),
                        React.createElement("div", { style: { marginLeft: 'auto', fontWeight: '500', color: '#1A1A1A', fontFamily: 'Barlow', fontSize: 14 } }, email)),
                    React.createElement("div", { style: { display: 'flex', marginTop: '10px', marginBottom: '10px' } },
                        React.createElement("div", { style: { marginRight: 'auto', fontWeight: '400', color: '#5F6368', fontFamily: 'Barlow', fontSize: 14 } }, "Role:"),
                        React.createElement("div", { style: { marginLeft: 'auto', fontWeight: '500', color: '#1A1A1A', fontFamily: 'Barlow', fontSize: 14 } }, state.role_name)),
                    React.createElement("div", { style: { display: 'flex', marginTop: '10px', marginBottom: '10px' } },
                        React.createElement("div", { style: { marginRight: 'auto', fontWeight: '400', color: '#5F6368', fontFamily: 'Barlow', fontSize: 14 } }, "Team:"),
                        React.createElement("div", { style: { marginLeft: 'auto', fontWeight: '500', color: '#1A1A1A', fontFamily: 'Barlow', fontSize: 14 } }, state.team_name)),
                    state.access_level === 'Admin' &&
                        React.createElement("div", { style: { display: 'flex', marginTop: '10px' } },
                            React.createElement("div", { style: { marginRight: 'auto', fontWeight: '400', color: '#5F6368', fontFamily: 'Barlow', fontSize: 14 } }, "Account Type:"),
                            React.createElement("div", { style: { marginLeft: 'auto', fontWeight: '500', color: '#2B7FED', fontFamily: 'Barlow', fontSize: 14 } },
                                React.createElement(ShieldOutlined, { style: { color: '#2B7FED' } }),
                                " Admin User"))),
                React.createElement("br", null),
                React.createElement("div", { className: "pt-5 pb-5", style: { width: '550px', marginLeft: 'auto', marginRight: 'auto' } },
                    React.createElement("div", null,
                        React.createElement("button", { style: {
                                backgroundColor: '#2B7FED',
                                color: '#FFFFFF',
                                width: '100%',
                                padding: '15px',
                                borderRadius: '4px',
                                marginBottom: '25px',
                                cursor: 'pointer'
                            }, onClick: handleContinueClick }, "CONTINUE TO LOGIN"),
                        React.createElement("br", null),
                        React.createElement("div", { style: { color: '#9E9E9E', fontSize: 11, fontFamily: 'Barlow', fontWeight: '400', textAlign: 'center' } }, "Use your email and the password you just created to sign in.")))))
        :
            React.createElement("div", { style: { backgroundColor: '#FAFAFA' } },
                React.createElement("img", { src: "/images/ss_logo_blue.png", className: "mb-10", alt: "SweetSpot", width: "225", height: "45" }),
                React.createElement("div", { style: { color: 'black', fontSize: 48, fontFamily: 'Barlow', fontWeight: '500', textAlign: 'center' } }, "User Info"),
                React.createElement("div", { style: { color: '#5F6368', fontSize: 18, fontFamily: 'Barlow', fontWeight: '400', textAlign: 'center' } }, "You are confirmed! Set your password and phone number to get started."),
                React.createElement("div", { style: { marginTop: '25px', width: '550px', marginLeft: 'auto', marginRight: 'auto', backgroundColor: '#FFFFFF', borderColor: '#E0E0E0', borderWidth: '1px', borderRadius: '4px', padding: '25px' } },
                    React.createElement("div", { style: { display: 'flex', marginBottom: '10px' } },
                        React.createElement("div", { style: { marginRight: 'auto', fontWeight: '400', color: '#5F6368', fontFamily: 'Barlow', fontSize: 14 } }, "Name:"),
                        React.createElement("div", { style: { marginLeft: 'auto', fontWeight: '500', color: '#1A1A1A', fontFamily: 'Barlow', fontSize: 14 } }, state.name)),
                    React.createElement("div", { style: { display: 'flex', marginTop: '10px', marginBottom: '10px' } },
                        React.createElement("div", { style: { marginRight: 'auto', fontWeight: '400', color: '#5F6368', fontFamily: 'Barlow', fontSize: 14 } }, "Email:"),
                        React.createElement("div", { style: { marginLeft: 'auto', fontWeight: '500', color: '#1A1A1A', fontFamily: 'Barlow', fontSize: 14 } }, email)),
                    React.createElement("div", { style: { display: 'flex', marginTop: '10px', marginBottom: '10px' } },
                        React.createElement("div", { style: { marginRight: 'auto', fontWeight: '400', color: '#5F6368', fontFamily: 'Barlow', fontSize: 14 } }, "Role:"),
                        React.createElement("div", { style: { marginLeft: 'auto', fontWeight: '500', color: '#1A1A1A', fontFamily: 'Barlow', fontSize: 14 } }, state.role_name)),
                    React.createElement("div", { style: { display: 'flex', marginTop: '10px', marginBottom: '10px' } },
                        React.createElement("div", { style: { marginRight: 'auto', fontWeight: '400', color: '#5F6368', fontFamily: 'Barlow', fontSize: 14 } }, "Team:"),
                        React.createElement("div", { style: { marginLeft: 'auto', fontWeight: '500', color: '#1A1A1A', fontFamily: 'Barlow', fontSize: 14 } }, state.team_name)),
                    state.access_level === 'Admin' &&
                        React.createElement("div", { style: { display: 'flex', marginTop: '10px' } },
                            React.createElement("div", { style: { marginRight: 'auto', fontWeight: '400', color: '#5F6368', fontFamily: 'Barlow', fontSize: 14 } }, "Account Type:"),
                            React.createElement("div", { style: { marginLeft: 'auto', fontWeight: '500', color: '#2B7FED', fontFamily: 'Barlow', fontSize: 14 } },
                                React.createElement(ShieldOutlined, { style: { color: '#2B7FED' } }),
                                " Admin User"))),
                React.createElement("br", null),
                React.createElement("div", { style: { width: '550px', marginLeft: 'auto', marginRight: 'auto' } },
                    React.createElement(FormProvider, { ...methods },
                        React.createElement("form", { onSubmit: handleSubmit(onSubmit), noValidate: true },
                            React.createElement("div", { className: 'row mb-5' },
                                React.createElement(FloatingLabel, { label: "New Password", htmlFor: "f-password", required: true, hasValue: !!password },
                                    React.createElement(FloatingFormInput, { type: "password", ...register('password', {
                                            required: 'New password is required',
                                        }), id: "f-password", required: true, autoComplete: "new-password" })),
                                React.createElement(FormError, { errors: errors, name: "f-password" })),
                            React.createElement("div", { className: 'row mb-5', style: { width: '100%', display: 'flex', justifyContent: 'space-between' } },
                                React.createElement(StatusPill, { label: "8+ Char.", status: passwordState.length }),
                                React.createElement(StatusPill, { label: "Uppercase", status: passwordState.uppercase }),
                                React.createElement(StatusPill, { label: "Number", status: passwordState.digit }),
                                React.createElement(StatusPill, { label: "Symbol", status: passwordState.special })),
                            React.createElement("div", { className: 'row mb-5' },
                                React.createElement(FloatingLabel, { label: "Confirm New Password", htmlFor: "f-password-confirmation", required: true, hasValue: !!passwordConfirmation },
                                    React.createElement(FloatingFormInput, { type: "password", ...register('passwordConfirmation', {
                                            required: 'Confirm password is required',
                                        }), id: "f-password-confirmation", autoComplete: "new-password", required: true })),
                                React.createElement(FormError, { errors: errors, name: "f-password-confirmation" })),
                            React.createElement("div", { className: 'row mb-5' },
                                React.createElement(FloatingLabel, { label: "Phone", htmlFor: "f-phone", required: true, hasValue: !!phone },
                                    React.createElement(FloatingFormInput, { type: "tel", id: "f-phone", required: true, value: phone, ...register('phone', {
                                            required: 'Numerical values only',
                                            pattern: {
                                                value: /^\d{3}-\d{3}-\d{4}$/,
                                                message: 'Invalid phone number',
                                            },
                                            onChange: handlePhoneChange
                                        }) })),
                                React.createElement(FormError, { errors: errors, name: "phone" })),
                            React.createElement("div", { className: "pt-5", style: { width: '550px' } },
                                React.createElement("div", { className: "flex" },
                                    React.createElement("button", { style: {
                                            backgroundColor: disableSubmit ? '#E0E0E0' : '#2B7FED',
                                            color: disableSubmit ? '#5F6368' : '#FFFFFF',
                                            width: '100%',
                                            padding: '15px',
                                            borderRadius: '4px',
                                            marginBottom: '25px'
                                        }, type: "submit", disabled: disableSubmit, className: disableSubmit ? 'cursor-not-allowed' : 'cursor-pointer' }, isSubmitting ? 'SUBMITTING...' : 'SUBMIT')))))))));
};
export default NewUserSetPassword;
